package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.viewModel
import com.ijoin.data.model.Ticket
import com.ijoin.i18n.strings
import com.ijoin.getPlatform
import com.ijoin.platform.isWebMobile
import com.ijoin.ui.components.MainBottomNavigationBar
import com.ijoin.ui.components.SideNavigationRail
import androidx.compose.material.icons.outlined.*
import androidx.compose.material.icons.filled.*
import com.ijoin.ui.components.TicketCard
import com.ijoin.ui.tickets.TicketsUiState
import com.ijoin.ui.tickets.TicketsViewModel
import com.ijoin.ui.components.RefreshableContainer
import com.ijoin.ui.components.rememberPullToRefreshState
import com.ijoin.ui.components.isAtTop
import androidx.compose.ui.platform.testTag
import com.ijoin.ui.test.TestTags
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventSearchField

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TicketsScreen(
    onTicketClick: (String) -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: TicketsViewModel = viewModel { TicketsViewModel() }
) {
    val strings = strings()
    val uiState by viewModel.uiState.collectAsState()
    val platform = getPlatform()
    val isWeb = platform.isWeb
    val isDesktopWeb = isWeb && !isWebMobile()
    val listState = rememberLazyListState()
    var searchQuery by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        viewModel.loadTickets()
    }

    if (isDesktopWeb) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .background(EventCardBackground)
                .testTag(TestTags.TicketsScreen.ROOT)
        ) {
            SideNavigationRail(
                selectedTab = "tickets",
                onTabSelected = { tab ->
                    when (tab) {
                        "home" -> onOpenHome()
                        "explore" -> onOpenExplore()
                        "create" -> onCreateEventClick()
                        "tickets" -> onOpenTickets()
                        "profile" -> onOpenProfile()
                    }
                },
                showAppStoreLinks = true
            )

            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.TopCenter
            ) {
                Column(modifier = Modifier.fillMaxWidth(0.6f)) {
                    TicketsTopBar(
                        isLoading = uiState.isLoading,
                        onRefresh = viewModel::refresh,
                        searchQuery = searchQuery,
                        onSearchQueryChange = { searchQuery = it }
                    )
                    TicketsContent(
                        uiState = uiState,
                        onTicketClick = onTicketClick,
                        searchQuery = searchQuery,
                        modifier = modifier,
                        listState = listState
                    )
                }
            }
        }
    } else {
        Scaffold(
            topBar = {
                TicketsTopBar(
                    isLoading = uiState.isLoading,
                    onRefresh = viewModel::refresh,
                    searchQuery = searchQuery,
                    onSearchQueryChange = { searchQuery = it }
                )
            },
            bottomBar = {
                MainBottomNavigationBar(
                    selectedTab = "tickets",
                    onTabSelected = { tab ->
                        when (tab) {
                            "home" -> onOpenHome()
                            "create" -> onCreateEventClick()
                            "tickets" -> onOpenTickets()
                            "profile" -> onOpenProfile()
                        }
                    }
                )
            },
            containerColor = EventCardBackground
        ) { paddingValues ->
            val pullState = rememberPullToRefreshState(
                refreshing = uiState.isLoading,
                onRefresh = viewModel::refresh,
                canRefresh = { listState.isAtTop() }
            )
            RefreshableContainer(
                modifier = modifier
                    .background(EventCardBackground)
                    .padding(paddingValues),
                state = pullState,
                enabled = true
            ) { pullModifier ->
                TicketsContent(
                    uiState = uiState,
                    onTicketClick = onTicketClick,
                    searchQuery = searchQuery,
                    modifier = pullModifier.testTag(TestTags.TicketsScreen.ROOT),
                    listState = listState
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TicketsTopBar(
    isLoading: Boolean,
    onRefresh: () -> Unit,
    searchQuery: String,
    onSearchQueryChange: (String) -> Unit
) {
    val strings = strings()
    Column {
        TopAppBar(
            title = { Text(strings.tickets) },
            actions = {
                IconButton(onClick = onRefresh) {
                    Icon(Icons.Default.Refresh, contentDescription = strings.refresh)
                }
            },
            colors = TopAppBarDefaults.topAppBarColors(
                containerColor = EventCardBackground,
                titleContentColor = EventCardAccentDark,
                actionIconContentColor = EventCardAccentDark
            )
        )

        EventSearchField(
            value = searchQuery,
            onValueChange = onSearchQueryChange,
            placeholder = strings.search,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp)
                .testTag(TestTags.TicketsScreen.SEARCH_FIELD)
        )

        if (isLoading) {
            LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun TicketsContent(
    uiState: TicketsUiState,
    onTicketClick: (String) -> Unit,
    searchQuery: String,
    modifier: Modifier = Modifier,
    listState: LazyListState
) {
    val strings = strings()
    val query = searchQuery.trim()
    val matchesQuery: (Ticket) -> Boolean = { ticket ->
        if (query.isBlank()) {
            true
        } else {
            listOfNotNull(
                ticket.eventTitle,
                ticket.eventLocation,
                ticket.ticketNumber
            ).any { it.contains(query, ignoreCase = true) }
        }
    }
    val filteredUpcoming = uiState.upcomingPaidTickets.filter(matchesQuery)
    val upcomingIds = filteredUpcoming.map(Ticket::id).toSet()
    val filteredTickets = uiState.tickets.filter(matchesQuery)
    val otherTickets = filteredTickets.filterNot { it.id in upcomingIds }

    Box(modifier = modifier.fillMaxSize()) {
        when {
            filteredTickets.isEmpty() && uiState.isLoading -> {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center))
            }
            filteredTickets.isEmpty() -> {
                EmptyTicketsState(
                    message = if (query.isBlank()) {
                        uiState.errorMessage ?: strings.ticketsEmptyMessage
                    } else {
                        strings.noResults
                    }
                )
            }
            else -> {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .testTag(TestTags.TicketsScreen.LIST),
                    state = listState,
                    contentPadding = PaddingValues(vertical = 16.dp)
                ) {
                    if (uiState.isOffline) {
                        item {
                            OfflineNotice(
                                text = strings.offlineTicketsNotice,
                                modifier = Modifier
                                    .padding(horizontal = 16.dp)
                                    .fillMaxWidth()
                            )
                        }
                    }

                    if (filteredUpcoming.isNotEmpty()) {
                        item {
                            SectionHeader(strings.upcomingPaidTicketsTitle)
                        }
                        items(filteredUpcoming) { ticket ->
                            TicketListItem(ticket = ticket, onTicketClick = onTicketClick)
                        }
                    }

                    if (otherTickets.isNotEmpty()) {
                        item {
                            SectionHeader(strings.otherTicketsTitle)
                        }
                        items(otherTickets) { ticket ->
                            TicketListItem(ticket = ticket, onTicketClick = onTicketClick)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionHeader(title: String) {
    Text(
        text = title,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
    )
}

@Composable
private fun TicketListItem(
    ticket: Ticket,
    onTicketClick: (String) -> Unit
) {
    TicketCard(
        ticket = ticket,
        onTicketClick = onTicketClick,
        onFavoriteClick = {},
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .testTag("${TestTags.TicketsScreen.TICKET_CARD_PREFIX}${ticket.id}")
    )
}

@Composable
private fun OfflineNotice(text: String, modifier: Modifier = Modifier) {
    Card(modifier = modifier, colors = CardDefaults.cardColors()) {
        Text(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            modifier = Modifier.padding(16.dp)
        )
    }
}

@Composable
private fun EmptyTicketsState(message: String) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyLarge,
            textAlign = androidx.compose.ui.text.style.TextAlign.Center
        )
    }
}
